import { RobDateStatusEnum } from 'src/enums/bunkering/robDateStatusEnum';
import { fuelTankTypes, getBunkeringsInAolGroup, getBunkeringsOnboardAtDate, getContentsInTankAtDate } from '../BunkeringUtils';
import { AolDataItem, AolGroup } from 'src/types/ViewModel/AolItem';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { addDateHours } from 'odfjell-date-time-utils/dateTimeUtils';
import { BunkerLoss } from 'src/types/ViewModel/Bunkering/BunkerLoss';
import { UUID } from 'crypto';

/** Maximum number of BDNs (Bunker Delivery Notes) allowed for OVDLA reporting */
export const MAX_BDN_COUNT = 8;

/**
 * Gets the count of unique bunker references on board at a specific date
 * @param bunkerings Array of all bunkerings
 * @param date The date to check
 * @returns Number of bunkerings on board at the specified date
 */
export const getBunkerRefCountAtDate = (bunkerings: Bunkering[], date: Date): number => {
    const bunkeringsOnboard = getBunkeringsOnboardAtDate(bunkerings, date);
    return bunkeringsOnboard.length;
};

/**
 * Soft warning: checks if bunker ref count is exactly at the OVDLA limit
 * @param bunkerings Array of all bunkerings
 * @param date The date to check
 * @returns True if bunker ref count equals MAX_BDN_COUNT (exactly 8)
 */
export const isAtBunkerRefLimit = (bunkerings: Bunkering[], date: Date): boolean => {
    return getBunkerRefCountAtDate(bunkerings, date) === MAX_BDN_COUNT;
};

/**
 * Severe warning: checks if bunker ref count exceeds the OVDLA limit
 * @param bunkerings Array of all bunkerings
 * @param date The date to check
 * @returns True if bunker ref count exceeds MAX_BDN_COUNT (more than 8)
 */
export const hasExceededBunkerRefLimit = (bunkerings: Bunkering[], date: Date): boolean => {
    return getBunkerRefCountAtDate(bunkerings, date) > MAX_BDN_COUNT;
};

/**
 * Counts the actual distinct bunker references that have content in tanks at a specific date.
 * Unlike getBunkerRefCountAtDate (status-based), this uses getContentsInTankAtDate which
 * checks whether a bunkering actually has ROB > 0 in at least one tank.
 */
export const getActualBunkerRefCount = (
    vessel: Vessel | null,
    bunkerings: Bunkering[],
    date: Date,
    aolGroup: AolGroup,
    aolDataItems: AolDataItem[],
    fuelRobData: FuelROB[]
): number => {
    if (!vessel) return 0;
    const allBunkerGuids = new Set<string>();
    vessel.nonCargoTanks
        .filter((tank) => fuelTankTypes.includes(tank.tankType))
        .forEach((tank) => {
            const contents = getContentsInTankAtDate(bunkerings, tank.tankId, date, aolGroup, aolDataItems, fuelRobData);
            contents.forEach((b) => allBunkerGuids.add(b.bunkeringGuid));
        });
    return allBunkerGuids.size;
};

export const getRobDateStatus = (
    vessel: Vessel | null,
    aolGroup: AolGroup,
    aolDataItems: AolDataItem[],
    date: Date,
    bunkerings: Bunkering[],
    fuelRobData: FuelROB[]
): RobDateStatusEnum => {
    if (!vessel) return RobDateStatusEnum.aolIncomplete;
    let newStatus = RobDateStatusEnum.aolIncomplete;

    if (aolGroup?.aolDataItems && aolGroup?.aolDataItems.length > 0) {
        const allAolsComplete = aolGroup.aolDataItems.every((aolItem) => aolItem?.noonReport?.enginesSection?.sectionData?.isCompleted);
        if (allAolsComplete) {
            newStatus = RobDateStatusEnum.incomplete;

            const missingRob = vessel.nonCargoTanks
                .filter((tank) => fuelTankTypes.includes(tank.tankType))
                .some((tank) => {
                    const contentInTank = getContentsInTankAtDate(
                        bunkerings,
                        tank.tankId,
                        addDateHours(date, 12),
                        aolGroup,
                        aolDataItems,
                        fuelRobData
                    );
                    const missingAoLRobData = contentInTank.some((bunkering) => {
                        return aolGroup.aolDataItems?.some((aolItem) => {
                            return !fuelRobData.some((fuelRob) => {
                                return (
                                    fuelRob.noonReportId &&
                                    fuelRob.tankId === tank.tankId &&
                                    fuelRob.bunkeringGuid === bunkering.bunkeringGuid &&
                                    fuelRob.noonReportId === aolItem.noonReport?.metadata?.noonReportId
                                );
                            });
                        });
                    });
                    const bunkeringsInAolPeriod = getBunkeringsInAolGroup(addDateHours(date, 12), bunkerings);
                    const missingBunkeringRobData = bunkeringsInAolPeriod.some((bunkering) => {
                        if (!bunkering.bunkerReceivedPerTanks.some((b) => b.tankId === tank.tankId)) return false;
                        return !fuelRobData.some((fuelRob) => {
                            return !fuelRob.noonReportId && fuelRob.tankId === tank.tankId && fuelRob.bunkeringGuid === bunkering.bunkeringGuid;
                        });
                    });
                    return missingAoLRobData || missingBunkeringRobData;
                });
            if (!missingRob) {
                // Only block complete status if bunker ref limit is exceeded (OVDLA export will fail)
                // At-limit warning (exactly 8) is shown only in the banner, not in status
                // Use content-based count (actual ROB in tanks) rather than status-based count
                if (getActualBunkerRefCount(vessel, bunkerings, addDateHours(date, 12), aolGroup, aolDataItems, fuelRobData) > MAX_BDN_COUNT) {
                    newStatus = RobDateStatusEnum.exceededBunkerRefLimit;
                } else {
                    newStatus = RobDateStatusEnum.complete;
                }
            }
        }
    }
    return newStatus;
};

export const getConsumptionData = (aolGroup: AolGroup, bunkering: Bunkering, tank: Tank, col: FuelRobColumn) => {
    let consumed = 0;
    const consumptionData = aolGroup.aolDataItems
        .flatMap((aolItem) => aolItem.noonReport?.enginesSection?.consumption)
        .concat(aolGroup.aolDataItems.flatMap((aolItem) => aolItem.noonReport?.inertGasProductionSection?.consumption))
        .concat(aolGroup.aolDataItems.flatMap((aolItem) => aolItem.noonReport?.powerpacksSection?.consumption));
    consumed = consumptionData.reduce((total, consumption) => {
        if (
            consumption?.bunkeringGuid === bunkering?.bunkeringGuid &&
            consumption?.tankId === tank.tankId &&
            consumption?.noonReportId === col.noonReportId
        ) {
            return total + consumption.consumptionMt;
        }
        return total;
    }, 0);

    consumed = parseFloat(consumed.toFixed(2));
    return consumed;
};

export const getLossData = (bunkerLossData: BunkerLoss[], tank: Tank, col: FuelRobColumn, bunkering: Bunkering) => {
    return (
        bunkerLossData
            .filter((l) => l.noonReportId === col.noonReportId && l.tankId === tank.tankId && l.bunkeringGuid === bunkering.bunkeringGuid)
            .reduce((acc, l) => acc + l.quantityMT, 0) * -1
    );
};

export const calculateRob = (previousColumn: FuelRobColumn, tank: Tank, bunkering: Bunkering, col: FuelRobColumn, consumed: number, loss: number) => {
    let calculatedRob =
        previousColumn?.fuelRobs?.find((fuelRob) => fuelRob.tankId === tank.tankId && fuelRob.bunkeringGuid === bunkering.bunkeringGuid)?.rob ?? 0;

    if (col.noonReportId) {
        calculatedRob = calculatedRob - consumed + loss;
    } else if (col.bunkeringGuid === bunkering.bunkeringGuid && col.debunkeringEventId) {
        const debunkered = bunkering.debunkeringData.find((b) => b.tankId === tank.tankId && b.debunkeringEventId === col.debunkeringEventId);
        calculatedRob -= debunkered?.bunkerReceivedMt ?? 0;
    } else if (col.bunkeringGuid === bunkering.bunkeringGuid) {
        const bunkerReceived = bunkering.bunkerReceivedPerTanks.find((b) => b.tankId === tank.tankId && !b.debunkeringEventId);
        calculatedRob += bunkerReceived?.bunkerReceivedMt ?? 0;
    }

    return calculatedRob >= 0 ? Number(calculatedRob.toFixed(2)) : 0;
};

export const findExistingRob = (fuelRobData: FuelROB[], tank: Tank, bunkering: Bunkering, col: FuelRobColumn) => {
    return fuelRobData?.find(
        (fuelRob) =>
            fuelRob.tankId === tank.tankId &&
            fuelRob.bunkeringGuid === bunkering.bunkeringGuid &&
            ((fuelRob.noonReportId && fuelRob.noonReportId === col.noonReportId) ||
                (!fuelRob.noonReportId && fuelRob.bunkeringGuid === col.bunkeringGuid))
    );
};

export const createFuelRob = (tank: Tank, bunkering: Bunkering, col: FuelRobColumn, calculatedRob: number, consumed: number, loss: number) => {
    return {
        fuelRobId: 0,
        tankId: tank.tankId,
        bunkeringGuid: bunkering.bunkeringGuid,
        noonReportId: col.noonReportId,
        rob: calculatedRob,
        consumed: consumed,
        loss: loss,
        calculatedRob: calculatedRob,
        displayOnly: col.bunkeringGuid && col.bunkeringGuid !== bunkering.bunkeringGuid,
    } as FuelROB;
};

export const getTanksWhereBunkerRefHasROB = (bunkeringGuid: UUID, fuelRobData: FuelROB[], bunkerings: Bunkering[]): number[] => {
    return fuelRobData.length > 0
        ? fuelRobData.filter((fuelRob) => fuelRob.bunkeringGuid === bunkeringGuid).map((fuelRob) => fuelRob.tankId)
        : bunkerings.find((b) => b.bunkeringGuid === bunkeringGuid)?.bunkerReceivedPerTanks?.map((b) => b.tankId);
};

export const compareTanks = (vessel: Vessel, tankIdA: number, tankIdB: number): number => {
    const tanks = vessel?.nonCargoTanks?.filter((tank) => fuelTankTypes.includes(tank.tankType));
    const a = tanks.find((tank) => tank.tankId === tankIdA);
    const b = tanks.find((tank) => tank.tankId === tankIdB);
    if (a.tankType !== b.tankType) {
        return a.tankType - b.tankType;
    }
    if (a.fuelTankType !== b.fuelTankType) {
        return a.fuelTankType - b.fuelTankType;
    }
    return a.tankName.localeCompare(b.tankName);
};

export const deafultSortFuelTanks = (vessel: Vessel): Tank[] => {
    const tanks = vessel?.nonCargoTanks?.filter((tank) => fuelTankTypes.includes(tank.tankType));
    return tanks.sort((a, b) => {
        if (a.tankType !== b.tankType) {
            return a.tankType - b.tankType;
        }
        if (a.fuelTankType !== b.fuelTankType) {
            return a.fuelTankType - b.fuelTankType;
        }
        return a.tankName.localeCompare(b.tankName);
    });
};
